import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '@willnq/shared/validation/authSchemas';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '@/auth/useAuth';
import { ROUTES } from '@/constants/routes';
import type { MfaChallengeState } from '@/features/identity/types/identity.types';
import { parseRestError } from '@/utils/errorUtils';

// `rememberMe` is a web-only UX field the backend `LoginRequest` doesn't declare (see
// `RememberMeCheckbox`'s doc comment) — extended locally so the form has somewhere to bind it,
// never sent to `authService.login`.
const loginFormSchema = loginSchema.extend({ rememberMe: z.boolean().optional() });
type LoginFormValues = z.infer<typeof loginFormSchema>;

/** Drives `LoginForm` — a successful `mfaRequired` result hands off to `MfaChallengePage` via
 * router state rather than a modal, since MFA is a distinct route (docs/development/FrontendGuidelines.md §3). */
export function useLoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  // Set when the account exists but is not Active, so the form can offer the one route out of it.
  // `User.NotActive` also covers a suspended or deactivated account, which is why this surfaces a
  // CHOICE ("verify, or contact support") rather than forwarding straight to the verify screen.
  const [needsVerification, setNeedsVerification] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    setNeedsVerification(false);
    try {
      const result = await login({ email: values.email, password: values.password });

      if (result.mfaRequired) {
        const state: MfaChallengeState = {
          method: 'password',
          email: values.email,
          password: values.password,
          enrollment: result.mfaSetup ?? undefined,
        };
        navigate(ROUTES.mfaChallenge, { state });
        return;
      }

      const from = (location.state as { from?: Location } | null)?.from;
      navigate(from ?? ROUTES.root, { replace: true });
    } catch (error) {
      const { code, message } = parseRestError(error);
      if (code === 'User.NotActive') {
        // NOT a password error: nothing about the password is wrong, and pinning it there left the
        // one account state with a real next step ("verify your email") reading as a typo, with no
        // route to the verify screen anywhere on the page.
        setNeedsVerification(true);
        setServerError(message);
      } else if (code === 'Identity.InvalidCredentials') {
        form.setError('password', { message });
      } else {
        setServerError(message);
      }
    }
  });

  return { form, onSubmit, serverError, needsVerification, email: form.getValues('email') };
}
